/* eslint-disable react/jsx-closing-bracket-location */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { Box, Stack } from '../LayoutPrimitives';
import Heading from '../Heading';
import Button from '../Button';
import {
    IconWarning,
    IconInfo,
    IconError,
    IconCheckCircle,
    IconClose,
} from '../icons/Icons';

const backgrounds = {
    warning: 'light-yellow',
    info: 'light-blue',
    error: 'light-red',
    success: 'light-green',
};

const borders = {
    warning: 'yellow',
    info: 'blue',
    error: 'red',
    success: 'green',
};

const icons = {
    warning: IconWarning,
    info: IconInfo,
    error: IconError,
    success: IconCheckCircle,
};

export default function Alert({
    children,
    heading,
    headingLevel = 2,
    type = 'warning',
    shouldFocusOnMount = false,
    className,
    hasBoxStyling = true,
    showCloseButton = false,
    onCloseButtonClick,
    ...rest
}) {
    const ref = useRef();

    useEffect(() => {
        let timer;

        if (shouldFocusOnMount) {
            timer = setTimeout(() => {
                ref.current.focus();
            }, 0);
        }

        return () => {
            clearTimeout(timer);
        };
    }, [shouldFocusOnMount]);

    const Icon = icons[type];

    return (
        <div
            data-component="Alert"
            ref={ref}
            tabIndex={shouldFocusOnMount ? '-1' : null}
            className={cx('sarsa-alert', type, className)}
            {...rest}
        >
            <Box
                className={cx({ 'show-close-button': showCloseButton })}
                background={hasBoxStyling ? backgrounds[type] : null}
                border={hasBoxStyling ? borders[type] : null}
                padding={hasBoxStyling ? 'sm' : null}
            >
                <Icon size="md" className={type} role="img" aria-label={`${type} icon`} />

                <Stack space="xxs">
                    {heading && (
                        <Heading headingLevel={headingLevel} appearance="h6">
                            {heading}
                        </Heading>
                    )}
                    <div className="alert-body">{children}</div>
                </Stack>

                {showCloseButton && (
                    <Button
                        className="sarsa-alert-close-button"
                        size="xs"
                        appearance="subtle"
                        aria-label="Dismiss alert"
                        iconBeforeElement={<IconClose />}
                        onClick={onCloseButtonClick}
                    />
                )}
            </Box>
        </div>
    );
}

Alert.propTypes = {
    /** Contents of the alert */
    children: PropTypes.node,

    /** Alert title */
    heading: PropTypes.string,

    /** The h* element to use (is heading is not Nil) */
    headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),

    /** Sets the color and icon */
    type: PropTypes.oneOf(['info', 'error', 'warning', 'success']),

    /** If true, the alert will try to gain focus when it is rendered */
    shouldFocusOnMount: PropTypes.bool,

    /** If false, the box border, background and padding are removed. (BYO styling container) */
    hasBoxStyling: PropTypes.bool,

    /** Additional class added to the outer container */
    className: PropTypes.string,

    /** Shows the close icon button */
    showCloseButton: PropTypes.bool,

    /** Close button click handler */
    onCloseButtonClick: PropTypes.func,
};
